<template>
    <AbstractMenuModal ref="abstract-menu"
                       :modal-i-d="modalID"
                       :resolve="resolve"
                       :reject="reject">
        <template v-slot:buttons>
            <a v-if="deviceSet"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Open Cash Drawer"
               @click.prevent="openCashDrawer">
                <i class="fa-solid fa-eject fa-fw"></i>
                {{ l('NAV.OPEN_CASH_DRAWER') }}
            </a>

            <a v-if="deviceSet"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Add Cash"
               @click.prevent="addToFloat">
                <i class="fa-solid fa-money-simple-from-bracket fa-fw"></i>
                {{ l('NAV.ADD_CASH_TO_TILL') }}
            </a>

            <a v-if="deviceSet"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Remove Cash"
               @click.prevent="removeFromFloat">
                <i class="fa-solid fa-money-from-bracket fa-fw"></i>
                {{ l('NAV.REMOVE_CASH_FROM_TILL') }}
            </a>

            <a v-if="showSearchModalButton"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Search Modal"
               @click.prevent="openSearchModal">
                <i class="fa-regular fa-search fa-fw"></i>
                {{ l('SEARCH') }}
            </a>

            <SyncMenuButton v-if="deviceSet"
                            @done="close" />

            <a v-if="deviceSet"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Download Orders"
               @click.prevent="downloadOrders">
                <i class="fa-solid fa-cloud-arrow-down fa-fw"></i>
                {{ l('NAV.DOWNLOAD_ORDERS') }}
            </a>

            <a v-if="deviceSet && viewPrivileges.reports"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Reports"
               @click.prevent="goToReports">
                <i class="fa-solid fa-chart-pie fa-fw"></i>
                {{ l('NAV.REPORTS') }}
            </a>

            <a v-if="deviceSet && showDojoReports()"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Dojo Reports"
               @click.prevent="goToDojoReports">
                <i class="fa-solid fa-chart-pie fa-fw"></i>
                {{ l('NAV.DOJO_REPORTS') }}
            </a>

            <a v-if="deviceSet"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Switch User"
               @click.prevent="switchUser">
                <i class="fa-solid fa-people-arrows fa-fw"></i>
                {{ l('NAV.SWITCH_USER') }}
            </a>

            <LogoutMenuButton @done="close" />
        </template>

        <template v-slot:bottom-buttons>
            <a v-if="deviceSet"
               class="btn btn-white"
               data-analytics-id="Menu Modal / Settings"
               @click.prevent="showSettings">
                <i class="fa-solid fa-gear fa-fw"></i>
                {{ l('NAV.SETTINGS') }}
            </a>

            <a class="btn btn-white"
               data-analytics-id="Menu Modal / About"
               @click.prevent="about">
                <i class="fa-solid fa-info-circle fa-fw"></i>
                {{ l('NAV.ABOUT') }}
            </a>

            <HelpMenuButton @done="close" />
        </template>
    </AbstractMenuModal>
</template>

<script>
import AbstractMenuModal from '@/vue/modals/Menu/AbstractMenuModal';
import HelpMenuButton from '@/vue/modals/Menu/buttons/HelpMenuButton';
import ItemType from '@/lib/Items/ItemType';
import LogoutMenuButton from '@/vue/modals/Menu/buttons/LogoutMenuButton';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import SyncMenuButton from '@/vue/modals/Menu/buttons/SyncMenuButton';
import { container } from '@/container';
import { mapGetters, mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'EposMenuModal',

    components: {
        AbstractMenuModal,
        HelpMenuButton,
        LogoutMenuButton,
        SyncMenuButton,
    },

    mixins: [
        ModalPropsMixin,
    ],

    computed: {
        ...mapState([
            'viewPrivileges',
        ]),
        ...mapState('cartOffers', [
            'couponsCount',
        ]),
        ...mapGetters([
            'deviceSet',
        ]),

        showSearchModalButton() {
            const getAllSearchData = container.saleableItemService.itemTypes;

            return (this.couponsCount
                || container.paymentMethodService.getByRef(PaymentMethodRef.TESCO_VOUCHER_UK)
                || getAllSearchData[ItemType.PRODUCT_FOOD]?.count
                || getAllSearchData[ItemType.PRODUCT]?.count
                || getAllSearchData[ItemType.GIFT_VOUCHER]?.count)
                && (this.deviceSet && this.viewPrivileges.sell);
        },
    },

    methods: {
        close() {
            this.$refs['abstract-menu']?.close();
        },

        openCashDrawer() {
            this.close();
            container.cashDrawerService.openCashDrawer();
        },

        addToFloat() {
            this.close();
            container.cashDrawerService.addToFloat();
        },

        removeFromFloat() {
            this.close();
            container.cashDrawerService.removeFromFloat();
        },

        openSearchModal() {
            this.close();
            container.vueModalFactory.openAndIgnoreResult(MODAL_NAMES__EPOS.SEARCH);
        },

        downloadOrders() {
            if (container.orderDownloader.downloadInProgress) {
                container.toastFactory.error('NAV.DOWNLOAD_ORDERS_DUPE');
                return;
            }

            let downloadingToast = container.toastFactory.spinner('NAV.DOWNLOADING_ORDERS');
            this.close();

            container.orderDownloader
                .download((downloadedOrderCount) => {
                    container.toastFactory.updateText(downloadingToast, downloadedOrderCount + ' orders downloaded so far.');
                })
                .then((downloadedOrderCount) => {
                    container.toastFactory.success(
                        'NAV.DOWNLOADED_ORDERS', downloadedOrderCount + ' orders downloaded.'
                    );
                })
                .catch((err) => {
                    console.error(err);
                    container.toastFactory.error('NAV.DOWNLOAD_ORDERS_FAILED', err.message);
                })
                .finally(() => {
                    container.toastFactory.clear(downloadingToast);
                });
        },

        goToReports() {
            this.close();
            container.navigationService.viewReports();
        },

        goToDojoReports() {
            this.close();
            container.navigationService.viewDojoReports();
        },

        switchUser() {
            this.close();
            container.deviceLockService.lockDevice();
        },

        logout() {
            container.dialogService
                .confirm('NAV.LOGOUT_CONFIRM')
                .then((confirmed) => {
                    if (confirmed) {
                        container.userService.logout();
                    }
                });
        },

        showDojoReports() {
            const paymentMethod = container.paymentMethodService.getByRef(PaymentMethodRef.CARD_DOJO_CHIP_AND_PIN);
            if (paymentMethod) {
                const c = container.currentDevice.device.paymentTerminalConfig;
                if (
                    c // Will be null if the config has not been configured yet in ProPoint.
                    && c.hasOwnProperty('card-dojo-chip-and-pin')
                    && c['card-dojo-chip-and-pin'].hasOwnProperty('supportsReports')
                    && c['card-dojo-chip-and-pin'].supportsReports
                ) {
                    return true;
                }
            }
            return false;
        },

        showSettings() {
            this.close();
            container.privilegesManager
                // Reusing a very similar permission.
                .requirePrivilege(PrivilegeNames.CHANGE_PRINT_SETTINGS)
                .then(() => {
                    container.vueModalFactory.openAndIgnoreResult(MODAL_NAMES__EPOS.SETTINGS);
                });
        },

        about() {
            this.close();
            container.vueModalFactory.openAndIgnoreResult(MODAL_NAMES__EPOS.ABOUT);
        },
    },
};
</script>
